// TradeMate Pro - Unified Employee Permission System
// Used by both Invite Employee and Add Employee forms

export const EMPLOYEE_MODULES = {
  // Core Modules (Default for all employees)
  DASHBOARD: {
    key: 'dashboard',
    label: 'Dashboard',
    description: 'View dashboard and analytics',
    category: 'Core',
    defaultEnabled: true
  },
  CALENDAR: {
    key: 'calendar',
    label: 'Calendar',
    description: 'View and manage schedule',
    category: 'Core',
    defaultEnabled: true
  },
  JOBS: {
    key: 'jobs',
    label: 'Jobs',
    description: 'View and manage jobs',
    category: 'Core',
    defaultEnabled: true
  },
  DOCUMENTS: {
    key: 'documents',
    label: 'Documents',
    description: 'Access documents and files',
    category: 'Core',
    defaultEnabled: true
  },

  // Sales & Customer Management
  CUSTOMERS: {
    key: 'customers',
    label: 'Customers',
    description: 'View and manage customer information',
    category: 'Sales',
    defaultEnabled: false
  },
  QUOTES: {
    key: 'quotes',
    label: 'Quotes',
    description: 'Create and manage quotes',
    category: 'Sales',
    defaultEnabled: false
  },
  INVOICES: {
    key: 'invoices',
    label: 'Invoices',
    description: 'View and manage invoices',
    category: 'Sales',
    defaultEnabled: false
  },
  INCOMING_REQUESTS: {
    key: 'incoming_requests',
    label: 'Incoming Requests',
    description: 'Manage incoming service requests',
    category: 'Sales',
    defaultEnabled: false
  },
  // Board #1245, operator decision 2026-09-20. TEAM CHAT IS NOT IN THIS LIST AND MUST NOT BE:
  // internal staff chat is available to every active employee, with no toggle, because there is
  // nothing to protect. What needed a permission is the company-to-CUSTOMER inbox, which a
  // technician could previously reach and send from with no permission and no job scope at all.
  //
  // NOT added to FULL_TIER_ONLY_MODULES on purpose. Crew are not shut out of customer messaging —
  // they are SCOPED to it: the RESTRICTIVE policy messages_crew_only_assigned_work limits a crew
  // seat to work orders assigned to them, whatever this toggle says. Putting it in the Full-tier
  // list would strip the toggle for crew entirely and remove the job-bound conversation the
  // decision explicitly grants them.
  CUSTOMER_MESSAGING: {
    key: 'customer_messaging',
    label: 'Customer Messaging',
    description: 'Use the customer inbox. Crew only ever see jobs assigned to them. Team Chat is separate and always available.',
    category: 'Sales',
    defaultEnabled: false
  },

  // HR & Team Management
  EMPLOYEES: {
    key: 'employees',
    label: 'Employees',
    description: 'Manage employee accounts',
    category: 'HR',
    defaultEnabled: false
  },
  TIMESHEETS: {
    key: 'timesheets',
    label: 'Time Tracking',
    description: 'Track hours logged against jobs',
    category: 'HR',
    defaultEnabled: true
  },
  PAYROLL: {
    key: 'payroll',
    label: 'Payroll',
    description: 'Access payroll information',
    category: 'HR',
    defaultEnabled: false
  },

  // Finance
  EXPENSES: {
    key: 'expenses',
    label: 'Expenses',
    description: 'Track and manage expenses',
    category: 'Finance',
    defaultEnabled: false
  },
  PURCHASE_ORDERS: {
    key: 'purchase_orders',
    label: 'Purchase Orders',
    description: 'Create and manage purchase orders',
    category: 'Finance',
    defaultEnabled: false
  },
  VENDORS: {
    key: 'vendors',
    label: 'Vendors',
    description: 'Manage vendor relationships',
    category: 'Finance',
    defaultEnabled: false
  },

  // Operations
  TOOLS: {
    key: 'tools',
    label: 'Tools',
    description: 'Access tools and utilities',
    category: 'Operations',
    defaultEnabled: true
  },
  INVENTORY: {
    key: 'inventory',
    label: 'Inventory',
    description: 'Manage inventory and stock',
    category: 'Operations',
    defaultEnabled: false
  },
  MARKETPLACE: {
    key: 'marketplace',
    label: 'Marketplace',
    description: 'Access contractor marketplace',
    category: 'Operations',
    defaultEnabled: false
  },

  // Admin
  REPORTS: {
    key: 'reports',
    label: 'Reports',
    description: 'View business reports and analytics',
    category: 'Admin',
    defaultEnabled: false
  },
  SETTINGS: {
    key: 'settings',
    label: 'Settings',
    description: 'Access company settings',
    category: 'Admin',
    defaultEnabled: false
  }
};

/**
 * Get default permissions based on role
 * @param {string} role - User role (owner, admin, manager, technician, etc.)
 * @returns {Object} - Permissions object with module keys and boolean values
 */
export const getDefaultPermissions = (role) => {
  const permissions = {};
  
  // Start with default values for each module
  Object.values(EMPLOYEE_MODULES).forEach(module => {
    permissions[module.key] = module.defaultEnabled;
  });

  // Role-based overrides
  const normalizedRole = role?.toLowerCase();

  // Owner, Admin, Manager - Full access to everything
  if (['owner', 'admin', 'manager'].includes(normalizedRole)) {
    Object.keys(permissions).forEach(key => {
      permissions[key] = true;
    });
  }
  
  // Supervisor - Most things except settings
  else if (normalizedRole === 'supervisor') {
    Object.keys(permissions).forEach(key => {
      permissions[key] = key !== 'settings'; // Everything except settings
    });
  }
  
  // Dispatcher - Core + Sales + Scheduling
  else if (normalizedRole === 'dispatcher') {
    // A dispatcher's whole job is telling customers when somebody is coming (board #1245).
    permissions.customer_messaging = true;
    permissions.dashboard = true;
    permissions.calendar = true;
    permissions.jobs = true;
    permissions.customers = true;
    permissions.quotes = true;
    permissions.incoming_requests = true;
    permissions.documents = true;
    permissions.timesheets = true;
    permissions.tools = true;
  }
  
  // Lead Technician - Core + Some Sales
  else if (normalizedRole === 'lead_technician') {
    permissions.dashboard = true;
    permissions.calendar = true;
    permissions.jobs = true;
    permissions.customers = true;
    permissions.quotes = true;
    permissions.documents = true;
    permissions.timesheets = true;
    permissions.expenses = true;
    permissions.tools = true;
    permissions.inventory = true;
  }
  
  // Accountant - Finance focused
  else if (normalizedRole === 'accountant') {
    permissions.dashboard = true;
    permissions.invoices = true;
    permissions.expenses = true;
    permissions.purchase_orders = true;
    permissions.vendors = true;
    permissions.payroll = true;
    permissions.reports = true;
    permissions.documents = true;
  }
  
  // Sales Rep - Sales focused
  else if (normalizedRole === 'sales_rep') {
    permissions.dashboard = true;
    permissions.customers = true;
    permissions.quotes = true;
    permissions.incoming_requests = true;
    permissions.documents = true;
    permissions.tools = true;
  }
  
  // Customer Service - Customer focused
  else if (normalizedRole === 'customer_service') {
    // The role is named after the thing this permission allows (board #1245).
    permissions.customer_messaging = true;
    permissions.dashboard = true;
    permissions.customers = true;
    permissions.quotes = true;
    permissions.jobs = true;
    permissions.incoming_requests = true;
    permissions.documents = true;
    permissions.tools = true;
  }
  
  // Technician, Apprentice, Helper - Core only (use defaults)
  // Already set by defaultEnabled values above

  return permissions;
};

/**
 * Get modules grouped by category for UI display
 * @returns {Object} - Object with category names as keys and arrays of modules as values
 */
export const getModulesByCategory = () => {
  const categories = {
    Core: [],
    Sales: [],
    HR: [],
    Finance: [],
    Operations: [],
    Admin: []
  };

  Object.values(EMPLOYEE_MODULES).forEach(module => {
    if (categories[module.category]) {
      categories[module.category].push(module);
    }
  });

  return categories;
};

/**
 * Get category display information
 * @returns {Object} - Category metadata for UI
 */
export const getCategoryInfo = () => {
  return {
    Core: {
      label: 'Core Modules',
      description: 'Essential features for all employees',
      icon: '🎯'
    },
    Sales: {
      label: 'Sales & Customers',
      description: 'Customer management and sales tools',
      icon: '💼'
    },
    HR: {
      label: 'HR & Team',
      description: 'Employee and team management',
      icon: '👥'
    },
    Finance: {
      label: 'Finance',
      description: 'Financial management and accounting',
      icon: '💰'
    },
    Operations: {
      label: 'Operations',
      description: 'Tools and operational features',
      icon: '⚙️'
    },
    Admin: {
      label: 'Administration',
      description: 'Reports and company settings',
      icon: '🔧'
    }
  };
};

/**
 * Generate a secure temporary password
 * @returns {string} - Temporary password
 */
export const generateTempPassword = () => {
  const uppercase = 'ABCDEFGHJKLMNPQRSTUVWXYZ'; // Removed I, O
  const lowercase = 'abcdefghijkmnopqrstuvwxyz'; // Removed l, o
  const numbers = '23456789'; // Removed 0, 1
  const special = '!@#$%';
  
  let password = '';
  
  // Ensure at least one of each type
  password += uppercase[Math.floor(Math.random() * uppercase.length)];
  password += lowercase[Math.floor(Math.random() * lowercase.length)];
  password += numbers[Math.floor(Math.random() * numbers.length)];
  password += special[Math.floor(Math.random() * special.length)];
  
  // Fill rest with random characters
  const allChars = uppercase + lowercase + numbers + special;
  for (let i = 4; i < 12; i++) {
    password += allChars[Math.floor(Math.random() * allChars.length)];
  }
  
  // Shuffle the password
  return password.split('').sort(() => Math.random() - 0.5).join('');
};


/**
 * SEAT TIER — the ceiling a role's permissions cannot exceed.
 *
 * Seat price is derived from users.role by the stripe-subscribe edge function, NOT from
 * permissions. So without a cap, an owner ticking "marketplace" for a technician grants a
 * $25 feature on a $5 seat — and the pricing page sells those exclusions explicitly
 * ("Quotes & Invoicing / Inventory & Purchasing / Analytics & Reports / Marketplace
 * Access" are listed as what Crew does NOT get). This makes the boundary the code's, not
 * just the marketing page's.
 *
 * Kept in step with FULL_SEAT_ROLES / CREW_SEAT_ROLES in
 * supabase/functions/stripe-subscribe/index.ts — if a role moves between seat tiers there,
 * it must move here too, or billing and access will disagree.
 */
// The split is P20 (docs/PRODUCT_DECISIONS.md), which puts customer_service in Crew.
// 73f074437 (2026-08-29) moved it to Full without a ruling; the operator re-confirmed P20 on
// 2026-10-24, so it is Crew again (0 users held it either time). The database owner is
// public.seat_tier(); tests/pure/seat-tier-p20.test.js keeps this copy identical to it.
export const CREW_SEAT_ROLES = ['lead_technician', 'technician', 'apprentice', 'helper', 'customer_service'];

/**
 * Modules a Crew seat can never hold, whatever the toggles say. Taken from the Crew
 * column of the pricing page rather than invented here.
 */
export const FULL_TIER_ONLY_MODULES = [
  'quotes', 'invoices', 'marketplace', 'reports',
  'inventory', 'purchase_orders', 'vendors', 'rate_cards',
  'payroll', 'employees', 'settings', 'labor',
];

/** True when this role is billed as a Crew ($5) seat. */
export const isCrewSeatRole = (role) => CREW_SEAT_ROLES.includes(String(role || '').toLowerCase());

/**
 * The permissions a user actually gets: role defaults, overlaid with any per-user
 * toggles, then CAPPED at the seat tier the role is billed under.
 *
 * The cap is applied LAST and deliberately cannot be overridden by a stored toggle — a
 * row written before the cap existed (or by a future UI bug) must not grant paid features
 * for free. To give someone a Full-tier module you change their ROLE, which reprices the
 * seat; that is the promotion path, and it is the only way the ceiling moves.
 */
export const getEffectivePermissions = (role, customPermissions = null) => {
  const permissions = { ...getDefaultPermissions(role) };

  if (customPermissions && typeof customPermissions === 'object') {
    Object.keys(permissions).forEach((key) => {
      if (typeof customPermissions[key] === 'boolean') permissions[key] = customPermissions[key];
    });
  }

  // Board #2857. App.js gates /labor on 'labor' and /rate-cards on 'rate_cards'
  // (simplePermissions MODULES), but EMPLOYEE_MODULES has no such keys and
  // employee_permissions has no such columns, so hasModuleAccess found undefined and every
  // non-owner — admin and manager included — was locked out of both. They are derived from
  // the permission that guards the same data: Labor Costs is employee pay rates (payroll),
  // rate cards are company pricing configuration (settings).
  permissions.labor = permissions.payroll === true;
  permissions.rate_cards = permissions.settings === true;

  if (isCrewSeatRole(role)) {
    FULL_TIER_ONLY_MODULES.forEach((key) => { permissions[key] = false; });
  }

  return permissions;
};
